import React from 'react';
import axios from 'axios';
import { Tree, TreeNode } from 'react-organizational-chart';
import { User, Crown, Star, UserPlus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export const Organigrama = ({ orgId }) => {
  const { user } = useAuth();
  
  const [members, setMembers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  
  React.useEffect(() => {
    const loadMembers = async () => {
      if (!orgId) return;
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/organizations/${orgId}/members`);
        setMembers(res.data.members || []);
      } catch (error) {
        console.error('Error loading members:', error);
        setMembers([]);
      } finally {
        setLoading(false);
      }
    };
    loadMembers();
  }, [orgId]);
  
  if (loading) {
    return <div className="text-white text-center py-12">Cargando organigrama...</div>;
  }
  
  const getRoleIcon = (role) => {
    if (role === 'owner' || role === 'admin') return <Crown size={16} className="text-yellow-500" />;
    if (role === 'lider') return <Star size={16} className="text-blue-500" />;
    return <User size={16} className="text-gray-500" />;
  };

  const getRoleColor = (role, isCurrentUser = false) => {
    if (isCurrentUser) return `from-yellow-500 to-amber-500`;
    if (role === 'owner' || role === 'admin') return 'from-yellow-600 to-amber-600';
    if (role === 'lider') return 'from-teal-500 to-cyan-600';
    return 'from-gray-500 to-gray-600';
  };

  const MemberCard = ({ member, isCurrentUser = false }) => (
    <div 
      className={`bg-gradient-to-br ${getRoleColor(member.role, isCurrentUser)} p-4 rounded-xl text-white shadow-lg min-w-[200px] ${
        isCurrentUser ? 'border-4 border-white' : ''
      }`}
    >
      <div className="flex items-center gap-2 mb-2">
        {member.picture ? (
          <img
            src={member.picture}
            alt={member.name}
            className="w-10 h-10 rounded-full object-cover border-2 border-white shrink-0"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-white/20 border-2 border-white flex items-center justify-center text-white font-bold shrink-0">
            {(member.name || member.email || '?').charAt(0).toUpperCase()}
          </div>
        )}
        <div className="flex flex-col">
          <div className="flex items-center gap-1">
            {getRoleIcon(member.role)}
            <span className="font-bold text-xs">{member.role?.toUpperCase() || 'MIEMBRO'}</span>
          </div>
          {isCurrentUser && <span className="text-xs">(TÚ)</span>}
        </div>
      </div>
      <div className="text-lg font-bold">{member.name || member.email}</div>
      <div className="text-xs opacity-80">{member.email}</div>
    </div>
  );

  const MockCard = ({ label, icon: Icon }) => (
    <div 
      className="bg-gradient-to-br from-gray-700 to-gray-800 p-4 rounded-xl text-white shadow-lg min-w-[200px] border-2 border-dashed border-gray-500 opacity-60"
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} className="text-gray-400" />
        <span className="font-bold text-sm text-gray-400">{label}</span>
      </div>
      <div className="text-lg font-bold text-gray-400">Vacante</div>
      <div className="text-xs text-gray-500">Click para invitar →</div>
    </div>
  );

  // Encontrar al usuario actual
  const currentUserMember = members.find(m => m.user_id === user?.user_id);
  
  // Si no hay miembros o solo está el usuario actual
  if (members.length === 0) {
    return (
      <div className="bg-gray-900 rounded-xl p-8 overflow-x-auto">
        <Tree
          lineWidth="2px"
          lineColor="#c9a84c"
          lineBorderRadius="10px"
          label={
            <MemberCard 
              member={{
                name: user?.name || user?.email,
                email: user?.email,
                role: 'owner'
              }}
              isCurrentUser={true}
            />
          }
        >
          <TreeNode label={<MockCard label="LÍDER" icon={Star} />} />
          <TreeNode label={<MockCard label="AGENTE" icon={User} />} />
          <TreeNode label={<MockCard label="AGENTE" icon={User} />} />
        </Tree>
        <div className="mt-6 text-center">
          <p className="text-gray-400 text-sm">
            <UserPlus className="inline mr-2" size={16} />
            Invita a miembros para construir tu organización
          </p>
        </div>
      </div>
    );
  }

  // Separar miembros por rol
  const owner = members.find(m => m.role === 'owner') || currentUserMember;
  const admins = members.filter(m => m.role === 'admin');
  const lideres = members.filter(m => m.role === 'lider');
  const agentes = members.filter(m => m.role === 'agente');

  if (!owner) {
    return (
      <div className="text-white text-center py-12">
        <p className="text-gray-400">No se pudo cargar la estructura organizacional</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 rounded-xl p-8 overflow-x-auto">
      <Tree
        lineWidth="2px"
        lineColor="#c9a84c"
        lineBorderRadius="10px"
        label={<MemberCard member={owner} isCurrentUser={owner.user_id === user?.user_id} />}
      >
        {/* Admins */}
        {admins.map((admin, i) => (
          <TreeNode key={`admin-${i}`} label={<MemberCard member={admin} isCurrentUser={admin.user_id === user?.user_id} />} />
        ))}
        
        {/* Líderes con sus agentes */}
        {lideres.map((lider, i) => {
          const agentesDelLider = agentes.filter(a => a.lider_id === lider.user_id);
          return (
            <TreeNode key={`lider-${i}`} label={<MemberCard member={lider} isCurrentUser={lider.user_id === user?.user_id} />}>
              {agentesDelLider.length > 0 ? (
                agentesDelLider.map((agente, j) => (
                  <TreeNode key={`agente-${i}-${j}`} label={<MemberCard member={agente} isCurrentUser={agente.user_id === user?.user_id} />} />
                ))
              ) : (
                <TreeNode key={`mock-agente-${i}`} label={<MockCard label="AGENTE" icon={User} />} />
              )}
            </TreeNode>
          );
        })}
        
        {/* Agentes sin líder */}
        {agentes.filter(a => !a.lider_id).map((agente, i) => (
          <TreeNode key={`agente-root-${i}`} label={<MemberCard member={agente} isCurrentUser={agente.user_id === user?.user_id} />} />
        ))}
        
        {/* Mock nodes si hay pocos miembros */}
        {lideres.length === 0 && (
          <TreeNode label={<MockCard label="LÍDER" icon={Star} />} />
        )}
      </Tree>
    </div>
  );
};
